import { supabase } from "@/integrations/supabase/client";
import type { FreightContext } from "@/utils/cepUtils";

export type { FreightContext };

export const FREIGHT_CONTEXT_LABELS: Record<FreightContext, string> = {
  event: "Eventos",
  order: "Produtos e Serviços",
};

/**
 * Resolve o contexto de frete a partir dos itens do carrinho.
 * Regra: somente itens de cardápio selecionável → "order"; qualquer item fixo (ou misto) → "event".
 */
export const resolveFreightContextFromCart = (
  items: Array<{ isSelectableMenu?: boolean }> | null | undefined,
): FreightContext => {
  if (!items || items.length === 0) return "event";
  return items.every((item) => !!item?.isSelectableMenu) ? "order" : "event";
};

/**
 * Resolve o contexto de frete a partir de events.menu_types.
 * Somente "selecao" → "order"; contém "fixo" (ou vazio) → "event".
 */
export const resolveFreightContextFromMenuTypes = (menuTypes: unknown): FreightContext => {
  const list = Array.isArray(menuTypes)
    ? menuTypes
    : typeof menuTypes === "string" && menuTypes.length > 0
      ? menuTypes.split(",")
      : [];

  const normalized = list.map((t) => String(t ?? "").trim().toLowerCase()).filter(Boolean);
  if (normalized.length === 0) return "event";

  const hasFixed = normalized.some((type) => type.includes("fixo"));
  const hasSelectable = normalized.some((type) => type.includes("selec"));

  return !hasFixed && hasSelectable ? "order" : "event";
};

/**
 * Busca as regiões de cobertura do fornecedor para um contexto.
 * Fallback: se o contexto "order" não tiver regiões configuradas, usa as de "event".
 */
export const fetchCoverageRowsForContext = async (
  supplierId: string,
  context: FreightContext,
  options?: { onlyEnabled?: boolean },
): Promise<any[]> => {
  if (!supplierId) return [];

  const buildQuery = (ctx: FreightContext) => {
    let query = (supabase as any)
      .from("supplier_cep_coverage")
      .select("*")
      .eq("supplier_id", supplierId)
      .eq("context", ctx);
    if (options?.onlyEnabled) query = query.eq("is_disabled", false);
    return query;
  };

  const { data, error } = await buildQuery(context);
  if (error) {
    console.error("[DeliveryContext] Erro ao buscar cobertura:", error);
    return [];
  }

  if ((data?.length ?? 0) > 0 || context === "event") return data || [];

  // Fallback para as regiões de eventos quando encomendas não estiver configurado
  const { data: fallback, error: fallbackError } = await buildQuery("event");
  if (fallbackError) {
    console.error("[DeliveryContext] Erro no fallback de cobertura:", fallbackError);
    return [];
  }
  return fallback || [];
};
